import {
  createResource,
  deleteResource,
  determineSelector,
  getResource,
  readUrl,
  readUrlObjectBased,
  throwOnApiError,
  updateResource,
  updateResourcePath,
} from "./api";
import { fetchWithTokenRefresh } from "./withTokenRefresh";

// Media outlets can be created via Rails API (/id/api)
// Reads come from PostgREST API
// create/getAll throw on error and return unwrapped data (a local convention predating the shared
// contract); `get` follows the standard read shape ({ data, totalRows } | { error }) like every other
// engine's get.
const create = async (attributes) => {
  const url = "/id/api/media_outlets";

  try {
    const result = await createResource(url, { data: { attributes } });

    if (result?.error) {
      const errors = result.error.body?.errors || [];
      const errorMessage = errors.length > 0 ? errors[0].detail : "Failed to create outlet";
      throw new Error(errorMessage);
    }

    // Unwrap JSON:API format: result.data contains { data: { id, attributes } }
    if (result?.data?.data?.attributes) {
      const { data } = result.data;
      return { id: data.id, ...data.attributes };
    }

    // If result.data is a plain object (not JSON:API wrapped), return it as-is
    if (result?.data && typeof result.data === "object" && Object.keys(result.data).length > 0) {
      return result.data;
    }

    throw new Error("Invalid response format from server");
  } catch (error) {
    // If it's already one of our errors, re-throw as-is
    if (error.message === "Invalid response format from server" || error.message.includes("Failed to create")) {
      throw error;
    }
    // Wrap other errors (like JSON parse errors) in a consistent message
    throw new Error(`Failed to create outlet: ${error.message}`);
  }
};

const getAll = async (options = { search: "", limit: 20 }) => {
  const { id = null, search = "", signal = null, limit = 20 } = options;

  // Validate search term length
  if (search && search.trim().length > 100) {
    throw new Error("Search term must be 100 characters or less");
  }

  // Build PostgREST query with object-based params
  const paginationParams = { limit: limit.toString() };
  const conditionParams = { order: "prestige.desc,name.asc" };
  const searchParams = {};

  if (id) {
    searchParams.id = `eq.${id}`;
  }

  if (search) {
    searchParams.name = `ilike.%${search.trim()}%`;
  }

  const selectParam = { select: "id,name,domain,prestige,visibility,profile_image_url" };

  const url = readUrlObjectBased("media_outlets", {
    paginationParams,
    conditionParams: { ...conditionParams, ...selectParam },
    searchParams,
  });

  const response = await fetchWithTokenRefresh(url, {
    method: "GET",
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.statusText || "Failed to load media outlets");
  }

  const data = await response.json();

  if (!Array.isArray(data)) {
    throw new Error("No data returned from server");
  }

  return data.map((e) => ({
    label: e.name,
    value: e.id,
    domain: e.domain,
    prestige: e.prestige,
    imageUrl: e.profile_image_url,
  }));
};

const get = async (idOrSlug) => {
  const selector = determineSelector(idOrSlug, { keyId: "id", keySlug: "slug" });
  const url = readUrl("media_outlets", { selector });
  return getResource(url);
};

// Batch-hydrate outlets by id (standard read shape, like promo_entities.getAllById).
const getAllById = async (ids) => {
  const url = readUrlObjectBased("media_outlets", { conditionParams: { id: `in.(${ids.join(",")})` } });
  return getResource(url);
};

// Standard mutation contract (NOT the local convention `create` above uses): throwOnApiError is
// applied at the first point the `{ error }` shape exists, so a failed save reaches the caller's
// try/catch instead of looking like a success. Renaming resets the slug server-side, so the caller
// has to follow the outlet to its new URL after the reload.
const updateById = async (idOrSlug, attributes) => {
  const url = updateResourcePath("media_outlets", idOrSlug);
  return throwOnApiError(await updateResource(url, { data: { attributes } }));
};

// The profile-image trio mirrors companies.js exactly — same nested `profile_image` route, same
// `{ data } | { error }` result — because <ProfileImage> routes through whichever of them owns the
// entity it was handed and can only do that if they answer alike.
const profileImagePath = (mediaOutletId) => `${updateResourcePath("media_outlets", mediaOutletId)}/profile_image`;

const createProfileImageById = async (mediaOutletId, attributes) => {
  return createResource(profileImagePath(mediaOutletId), { data: { attributes } });
};

const updateProfileImageById = async (mediaOutletId, attributes) => {
  return updateResource(profileImagePath(mediaOutletId), { data: { attributes } });
};

const deleteProfileImageById = async (mediaOutletId) => {
  return deleteResource(profileImagePath(mediaOutletId));
};

export default {
  create,
  createProfileImageById,
  deleteProfileImageById,
  get,
  getAll,
  getAllById,
  updateById,
  updateProfileImageById,
};
